import { Slider, Upload, UploadProps } from 'antd';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import styles from './index.module.scss';
import { faceStyleIcon, imageStyleIcon } from '../../../assets/ImageCreation';
import deleteHoverIcon from '../../../assets/ImageCreation/delete-red.svg';
import deleteIcon from '../../../assets/ImageCreation/delete.svg';
import editIcon from '../../../assets/ImageCreation/edit.svg';
import {
  DEFAULT_STYLE_WEIGHT,
  DEFAULT_FACE_WEIGHT,
  MAX_IMAGE_SIZE,
} from '../../../constants/image';
import { useStyle } from '../../../hooks/useStyle';
import { useAppDispatch, useAppSelector } from '../../../state';
import {
  Global_Image_Memory,
  setCropingImgUrl,
  setCurrStyleName,
  setImgConfig,
  setImgPreviewExtra,
} from '../../../state/image';
import { track } from '../../../track';
import { LocalImage } from '../../Image';
import { Title } from '../TitleTip';

const PAGE = 'image_generation_page';

const SliderStyles = {
  track: {
    height: '6px',
  },
  rail: {
    height: '6px',
  },
  handle: {
    insetBlockStart: '1.5px',
  },
};

export function ImageGuidance({
  onShowStyleReferenceBar,
  onShowImageCropModal,
  cdnPrefix,
  source,
}: {
  onShowStyleReferenceBar: () => void;
  onShowImageCropModal: () => void;
  cdnPrefix: string;
  source: string;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { web_preview_extra, avatar_style_id_list } = useAppSelector(
    (state) => state.image.imgConfig
  );

  const { styleName, picUrl } = useStyle({ avatar_style_id: avatar_style_id_list?.[0] });

  useEffect(() => {
    if (avatar_style_id_list?.length && !web_preview_extra?.style_img_url) {
      dispatch(setCurrStyleName(styleName));
      dispatch(setImgPreviewExtra({ style_img_url: picUrl }));
    }
  }, [styleName, picUrl]);

  const { currStyleName } = useAppSelector((state) => state.image.avatarStyle);

  const [styleWeight, setStyleWeight] = useState(
    web_preview_extra?.style_weight || DEFAULT_STYLE_WEIGHT
  );
  const [faceWeight, setFaceWeight] = useState(
    web_preview_extra?.face_weight || DEFAULT_FACE_WEIGHT
  );

  const styleImage = useMemo(() => {
    return web_preview_extra?.style_img_url;
  }, [web_preview_extra?.style_img_url]);

  const faceImage = useMemo(() => {
    return web_preview_extra?.face_img_url;
  }, [web_preview_extra?.face_img_url]);

  const onStyleWeightChange = (value: number) => {
    setStyleWeight(value);
  };

  const onFaceWeightChange = (value: number) => {
    setFaceWeight(value);
  };

  const onStyleWeightChangeComplete = (value: number) => {
    dispatch(setImgPreviewExtra({ style_weight: value }));
    track.send('style_reference_intensity_input', source, {
      style_reference_intensity: value,
    });
  };

  const onDeleteStyleImage = () => {
    setStyleWeight(DEFAULT_STYLE_WEIGHT);
    dispatch(setImgPreviewExtra({ style_img_url: undefined, style_weight: DEFAULT_STYLE_WEIGHT }));
    dispatch(setImgConfig({ avatar_style_id_list: [] }));
    dispatch(setCurrStyleName(''));
  };

  const onFaceWeightChangeComplete = (value: number) => {
    dispatch(setImgPreviewExtra({ face_weight: value }));
    track.send('face_reference_intensity_input', source, {
      face_reference_intensity: value,
    });
  };

  const onDeleteFaceImage = () => {
    setFaceWeight(DEFAULT_FACE_WEIGHT);
    dispatch(setImgPreviewExtra({ face_img_url: undefined, face_weight: DEFAULT_FACE_WEIGHT }));
  };

  const props: UploadProps = useMemo(
    () => ({
      name: 'file',
      accept: 'image/png,image/jpg,image/jpeg',
      customRequest: async (options) => {
        const { file } = options;

        if ((file as File).size > MAX_IMAGE_SIZE) {
          toast.error(t('upload_image_size_exceed'));
          return;
        }

        try {
          // 图片在裁剪前存本地，裁剪确认后再上传
          const url = (file as File).name;
          Global_Image_Memory[url] = file as File;

          dispatch(setCropingImgUrl(url));
          onShowImageCropModal();
        } catch (error) {
          console.error('upload image failed', error);
        }
      },
      itemRender: () => null,
    }),
    [dispatch, onShowImageCropModal, t]
  );

  const handleStyleImageClick = () => {
    onShowStyleReferenceBar();
    track.send('style_reference_click', PAGE);
  };

  const handleFaceImageClick = () => {
    track.send('face_reference_click', PAGE);
  };

  return (
    <div className={styles.guidance}>
      {/* 画风参数 */}
      <div className={styles.styleItem}>
        <div className={styles.image} onClick={handleStyleImageClick}>
          <img
            className={styles.imageItem}
            src={styleImage || imageStyleIcon.src}
            alt="style image"
          />
          {currStyleName && <div className={styles.styleName}>{currStyleName}</div>}
          {styleImage && (
            <div className={styles.edit}>
              <LocalImage src={editIcon} alt="edit icon" />
            </div>
          )}
        </div>
        <div className={styles.info}>
          <Title title={t('style_reference')} desc={t('style_reference_tip')} styles={styles} />
          <div className={styles.slider}>
            <Slider
              className={styles.sliderBar}
              value={styleWeight}
              min={0}
              max={1}
              step={0.1}
              styles={SliderStyles}
              disabled={!styleImage}
              onChange={onStyleWeightChange}
              onChangeComplete={onStyleWeightChangeComplete}
            />
            <div className={styles.sliderValue}>{styleWeight}</div>
          </div>
          {styleImage && (
            <div className={styles.deleteIcon} onClick={onDeleteStyleImage}>
              <LocalImage src={deleteIcon} alt="delete icon" />
            </div>
          )}
        </div>
      </div>
      {/* 面部参数 */}
      <div className={styles.styleItem}>
        <Upload {...props} className={styles.upload}>
          <div className={styles.image} onClick={handleFaceImageClick}>
            <img
              className={styles.imageItem}
              src={faceImage || faceStyleIcon.src}
              alt="face image"
            />
            {faceImage && (
              <div className={styles.edit}>
                <LocalImage src={editIcon} alt="edit icon" />
              </div>
            )}
          </div>
        </Upload>
        <div className={styles.info}>
          <Title title={t('face_reference')} desc={t('face_reference_tip')} styles={styles} />
          <div className={styles.slider}>
            <Slider
              className={styles.sliderBar}
              value={faceWeight}
              min={0}
              max={1}
              step={0.1}
              styles={SliderStyles}
              disabled={!faceImage}
              onChange={onFaceWeightChange}
              onChangeComplete={onFaceWeightChangeComplete}
            />
            <div className={styles.sliderValue}>{faceWeight}</div>
          </div>
          {faceImage && (
            <div className={styles.deleteIcon} onClick={onDeleteFaceImage}>
              <LocalImage src={deleteIcon} alt="delete icon" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
